import * as i2 from '@angular/common';
import { CommonModule, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Renderer2, ElementRef, Injector, HostListener, ViewChild, Output, Input, Inject, ViewEncapsulation, Component, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i3 from '@angular/forms';
import { NG_VALUE_ACCESSOR, Validators, NgControl, NgModel, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/material/core';
import { MatNativeDateModule, MAT_DATE_FORMATS, MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MatCalendar, MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon, MatDatepickerModule } from '@angular/material/datepicker';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { InputSharedPropertiesBase, DirectivesModule } from '@windmill/ng-windmill/shared';

const WINDMILL_DATE_PICKER_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => WindmillDatePickerComponent)
};
const NEGATIVE_NUMBER = -1;
const DEFAULT_MIN_DATE = new Date('0001-01-01');
const DATE_PICKER_ICON_SIZE = {
    default: '24',
    extrasmall: '20'
};
const materialComponents$1 = [
    MatCalendar,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepickerToggleIcon,
    MatDatepickerModule,
    MatNativeDateModule
];
/**
 * To use this component in a module, you can import the `WindmillDatePickerModule`:
 * @example
 * ```typescript
 * import { WindmillDatePickerModule } from '@windmill/ng-windmill/date-picker';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillDatePickerComponent`:
 * @example
 * ```typescript
 * import { WindmillDatePickerComponent } from '@windmill/ng-windmill/date-picke';
 * ```
 */
class WindmillDatePickerComponent extends InputSharedPropertiesBase {
    injector;
    windmillDateAdapter;
    changeDetectorRef;
    /**
     * Whether the input and pop-up should be disabled
     */
    disableDatepicker = false;
    /**
     * Whether the input should be disabled
     */
    disableInput = false;
    /**
     * The date picker that this input is associated with
     */
    pickerName;
    /**
     * The view that the calendar should start in
     */
    startView = 'month';
    /**
     * The date to open the calendar to initially
     */
    startAt;
    /**
     * The maximum selectable date
     */
    max;
    /**
     * The date picker calendar will contain only years/months
     */
    pickerType = 'default';
    /**
     * Show just the calendar component
     */
    calendarOnly = false;
    /**
     * To set the border for calendar component
     */
    hasBorder = true;
    /**
     * To set the shadow for calendar component
     */
    hasShadow = false;
    /**
     * Name for input
     */
    name;
    /**
     * Whether or not an input should have autocomplete enable
     */
    autocomplete = 'off';
    /**
     * Custom function for date validation.
     * When the function returns `false` it means that the date is invalid and it will be disabled in the calendar.
     */
    datepickerFilter;
    /**
     * The minimum selectable date
     */
    get min() {
        return this._minValue;
    }
    set min(value) {
        this._minValue = value === null ? DEFAULT_MIN_DATE : value;
    }
    /**
     * Custom CSS background color (e.g. hex, rgba, CSS variable etc.) for the date picker in an editable state
     */
    backgroundColor;
    /**
     * Emits selected year in multiyear view
     */
    yearSelected = new EventEmitter();
    /**
     * Emits selected month in year view
     */
    monthSelected = new EventEmitter();
    /**
     * Emits selected date
     */
    selectedDate = new EventEmitter();
    /**
     * Emits whenever the value changes due to the user typing or selecting a date from the calendar
     */
    dateInput = new EventEmitter();
    /**
     * Emits whenever the user finishes typing in the input (on input blur), or when the user chooses a date from the calendar
     */
    dateChange = new EventEmitter();
    /**
     * Emits the value when input has lost focus
     */
    innerBlur = new EventEmitter();
    /**
     * Emits whether the calendar is open
     */
    opened = new EventEmitter();
    /**
     * Emits whether the calendar is closed
     */
    closed = new EventEmitter();
    inputElement;
    inputReadonlyElement;
    pickerNameElement;
    isOpened = false;
    value;
    hasClearButton = false;
    _renderer2;
    _dateFormats;
    _minValue = DEFAULT_MIN_DATE;
    get isFormControlRequired() {
        return this.getFormControl().hasValidator(Validators.required) && !this.disabled && !this.readonly;
    }
    get readonlyValue() {
        const dateFormat = String(this._dateFormats.display.dateInput);
        return this.value ? this.windmillDateAdapter?.format(this.value, dateFormat) : null;
    }
    get readonlyPlaceholder() {
        return this.value ? '' : '-';
    }
    get hasBorderClass() {
        return this.hasBorder && !this.hasShadow;
    }
    get placeholderValue() {
        return this.readonly ? '-' : this.placeholder;
    }
    get tabIndexValue() {
        return this.disableInput || this.disableDatepicker ? NEGATIVE_NUMBER : 0;
    }
    get showClearButton() {
        return this.hasClearIcon && this.hasValue && !this.disableDatepicker;
    }
    get hasValue() {
        return Boolean(this.inputElement?.nativeElement?.value);
    }
    get datePickerIconSize() {
        return this.size === 'extrasmall' ? DATE_PICKER_ICON_SIZE.extrasmall : DATE_PICKER_ICON_SIZE.default;
    }
    get isInvalidDatePicker() {
        if (typeof this.validationFunction !== 'undefined') {
            return this.validationFunction();
        }
        return false;
    }
    get isDisabled() {
        if (this.readonly) {
            return false;
        }
        else if (this.disableInput) {
            return this.disableInput;
        }
        return this.disableDatepicker;
    }
    get isDisabledDatePickerPopup() {
        return this.disableInput ? false : this.disableDatepicker;
    }
    get backgroundColorValue() {
        return this.isDisabled || this.calendarOnly ? null : this.backgroundColor;
    }
    get showValidationMessages() {
        return this.isInvalidDatePicker && !this.readonly;
    }
    constructor(injector, dateFormats, windmillDateAdapter, changeDetectorRef) {
        const renderer2 = injector.get(Renderer2);
        const elementRef = injector.get(ElementRef);
        super(renderer2, elementRef);
        this.injector = injector;
        this.windmillDateAdapter = windmillDateAdapter;
        this.changeDetectorRef = changeDetectorRef;
        this._renderer2 = renderer2;
        this._dateFormats = dateFormats;
    }
    ngAfterViewChecked() {
        const hasClearButtonSnapshot = this.showClearButton;
        if (hasClearButtonSnapshot !== this.hasClearButton) {
            this.hasClearButton = hasClearButtonSnapshot;
            this.changeDetectorRef.detectChanges();
        }
    }
    onPopState() {
        this.pickerNameElement?.close();
    }
    getFormControl() {
        return this.injector.get(NgControl).control;
    }
    writeValue(newValue) {
        this.value = newValue;
    }
    setDisabledState(isDisabled) {
        this.disableDatepicker = isDisabled;
    }
    updateValue(event) {
        this.writeValue(event.target.value);
        const oldValue = new Date(this.getFormControl().value).getTime();
        const newValue = new Date(event.target.value).getTime();
        if (oldValue !== newValue) {
            this.onChange(event.target.value);
        }
        this.dateChange.emit(event);
    }
    chosenDate(picker, event) {
        this.writeValue(event);
        this.getFormControl().setValue(this.value);
        this.selectedDate.emit(event);
        picker.close();
    }
    onYearSelected(event) {
        this.emitYearSelected(event);
        if (this.pickerType === 'year') {
            this.chosenDate(this.pickerNameElement, event);
        }
    }
    emitYearSelected(event) {
        this.yearSelected.emit(event);
    }
    onMonthSelected(event) {
        this.emitMonthSelected(event);
        if (this.pickerType === 'month') {
            this.chosenDate(this.pickerNameElement, event);
        }
    }
    emitMonthSelected(event) {
        this.monthSelected.emit(event);
    }
    onSelectedChange(event) {
        this.writeValue(event);
        this.selectedDate.emit(event);
        this.onChange(this.value);
    }
    onKeyDownEnter(event) {
        event.preventDefault();
    }
    onInputChange(event) {
        event.stopPropagation();
        this.dateInput.emit(event);
        this.getFormControl().markAsDirty();
    }
    onDateInput(event) {
        if (this.injector.get(NgControl) instanceof NgModel) {
            this.onChange(event.target.value);
        }
    }
    onDateChange(event) {
        if (event.value === null) {
            this._onDateChangeWithNull();
        }
        else {
            this._onDateChangeWithAValidValue(event);
        }
    }
    onBlur() {
        this.innerBlur.emit(this.value);
        this.onTouched();
    }
    onClearValue() {
        this.getFormControl().markAsDirty();
        this.getFormControl().patchValue(null);
        this.inputElement.nativeElement.focus();
        this.dateChange.emit(null);
    }
    onCloseCalendar(event) {
        this.closed.emit(event);
        this.onTouched();
    }
    onOpenCalendar(event) {
        this.opened.emit(event);
        this.addCustomClasses();
    }
    _onDateChangeWithNull() {
        this.dateChange.emit(null);
        this.writeValue(null);
    }
    _onDateChangeWithAValidValue(event) {
        if (this.min && event.value < this.min) {
            event.target.value = this.min;
        }
        if (this.max && event.value > this.max) {
            event.target.value = this.max;
        }
        this.updateValue(event);
    }
    addCustomClasses() {
        setTimeout(() => {
            const popupOverlayElement = document.getElementsByClassName('mat-datepicker-popup')[0];
            if (popupOverlayElement) {
                this._updateClasses(popupOverlayElement);
            }
        });
    }
    _updateClasses(popupOverlayElement) {
        this._renderer2.addClass(popupOverlayElement, 'windmill-datepicker-popup');
        switch (this.size) {
            case 'small':
                this._renderer2.addClass(popupOverlayElement, 'small-size-popup');
                break;
            case 'extrasmall':
                this._renderer2.addClass(popupOverlayElement, 'extrasmall-size-popup');
                break;
            default:
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDatePickerComponent, deps: [{ token: forwardRef(() => Injector) }, { token: MAT_DATE_FORMATS }, { token: i1.DateAdapter }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: WindmillDatePickerComponent, isStandalone: true, selector: "windmill-date-picker", inputs: { disableDatepicker: "disableDatepicker", disableInput: "disableInput", pickerName: "pickerName", startView: "startView", startAt: "startAt", max: "max", pickerType: "pickerType", calendarOnly: "calendarOnly", hasBorder: "hasBorder", hasShadow: "hasShadow", name: "name", autocomplete: "autocomplete", datepickerFilter: "datepickerFilter", min: "min", backgroundColor: "backgroundColor" }, outputs: { yearSelected: "yearSelected", monthSelected: "monthSelected", selectedDate: "selectedDate", dateInput: "dateInput", dateChange: "dateChange", innerBlur: "innerBlur", opened: "opened", closed: "closed" }, host: { listeners: { "window:popstate": "onPopState()" } }, providers: [WINDMILL_DATE_PICKER_VALUE_ACCESSOR, { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["inputElement"], descendants: true }, { propertyName: "inputReadonlyElement", first: true, predicate: ["inputReadonlyElement"], descendants: true }, { propertyName: "pickerNameElement", first: true, predicate: ["pickerName"], descendants: true }], usesInheritance: true, ngImport: i0, template: "@if (readonly) {\r\n  <div\r\n    class=\"windmill-input is-readonly\"\r\n    [class.has-value]=\"value\"\r\n    [ngClass]=\"size\">\r\n    <input\r\n      #inputReadonlyElement\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [ngModel]=\"readonlyValue\"\r\n      [readonly]=\"readonly\"\r\n      [attr.title]=\"title\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testInputId\"\r\n      [attr.placeholder]=\"readonlyPlaceholder\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\" />\r\n  </div>\r\n} @else {\r\n  <div\r\n    class=\"windmill-date-picker\"\r\n    [ngClass]=\"size\"\r\n    [class.windmill-input]=\"!calendarOnly\"\r\n    [class.is-disabled]=\"isDisabled\"\r\n    [class.has-value]=\"value\"\r\n    [class.input-invalid]=\"isInvalidDatePicker\"\r\n    [class.has-clear-icon]=\"hasClearIcon\"\r\n    [style.backgroundColor]=\"backgroundColorValue\">\r\n    @if (calendarOnly) {\r\n      <mat-calendar\r\n        class=\"windmill-calendar standalone-calendar\"\r\n        [class.has-border]=\"hasBorderClass\"\r\n        [class.has-shadow]=\"hasShadow\"\r\n        [startView]=\"startView\"\r\n        [startAt]=\"startAt\"\r\n        [selected]=\"value\"\r\n        [minDate]=\"min\"\r\n        [maxDate]=\"max\"\r\n        [dateFilter]=\"datepickerFilter\"\r\n        (yearSelected)=\"emitYearSelected($event)\"\r\n        (monthSelected)=\"emitMonthSelected($event)\"\r\n        (selectedChange)=\"onSelectedChange($event)\">\r\n      </mat-calendar>\r\n    } @else {\r\n      <input\r\n        #inputElement\r\n        class=\"input-element\"\r\n        [formControl]=\"getFormControl()\"\r\n        [id]=\"id\"\r\n        [min]=\"min\"\r\n        [max]=\"max\"\r\n        [matDatepicker]=\"pickerName\"\r\n        [matDatepickerFilter]=\"datepickerFilter\"\r\n        [attr.placeholder]=\"placeholderValue\"\r\n        [tabindex]=\"tabIndexValue\"\r\n        [disabled]=\"isDisabled\"\r\n        [name]=\"name\"\r\n        [autocomplete]=\"autocomplete\"\r\n        [attr.title]=\"title\"\r\n        [attr.data-testid]=\"testInputId\"\r\n        [attr.aria-label]=\"ariaLabel\"\r\n        [attr.aria-labelledby]=\"ariaLabelledby\"\r\n        [attr.aria-description]=\"ariaDescription\"\r\n        [attr.aria-describedby]=\"ariaDescribedby\"\r\n        [attr.aria-invalid]=\"isInvalidDatePicker\"\r\n        [attr.aria-required]=\"isFormControlRequired\"\r\n        (keydown.enter)=\"onKeyDownEnter($event)\"\r\n        (input)=\"onInputChange($event)\"\r\n        (dateInput)=\"onDateInput($event)\"\r\n        (dateChange)=\"onDateChange($event)\"\r\n        (blur)=\"onBlur()\" />\r\n      @if (hasClearButton) {\r\n        <button\r\n          class=\"clear-button\"\r\n          type=\"button\"\r\n          [attr.aria-label]=\"ariaLabelClearIcon\"\r\n          (click)=\"onClearValue()\">\r\n          <centric-icon2\r\n            class=\"clear-icon\"\r\n            [size]=\"'16'\"\r\n            [svgIcon]=\"'cancel_r'\">\r\n          </centric-icon2>\r\n        </button>\r\n      }\r\n      <mat-datepicker-toggle\r\n        matSuffix\r\n        [for]=\"pickerName\"\r\n        [disabled]=\"disableDatepicker\">\r\n        <centric-icon2\r\n          class=\"icon-date-picker\"\r\n          matDatepickerToggleIcon\r\n          [class.disabled]=\"disableDatepicker\"\r\n          [svgIcon]=\"'calendar_b'\"\r\n          [size]=\"datePickerIconSize\">\r\n        </centric-icon2>\r\n      </mat-datepicker-toggle>\r\n      <mat-datepicker\r\n        #pickerName\r\n        [panelClass]=\"'windmill-calendar'\"\r\n        [startView]=\"startView\"\r\n        [startAt]=\"startAt\"\r\n        [disabled]=\"isDisabledDatePickerPopup\"\r\n        [opened]=\"isOpened\"\r\n        (yearSelected)=\"onYearSelected($event)\"\r\n        (monthSelected)=\"onMonthSelected($event)\"\r\n        (closed)=\"onCloseCalendar($event)\"\r\n        (opened)=\"onOpenCalendar($event)\">\r\n      </mat-datepicker>\r\n    }\r\n  </div>\r\n}\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (showValidationMessages) {\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i3.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }, { kind: "component", type: MatCalendar, selector: "mat-calendar", inputs: ["headerComponent", "startAt", "startView", "selected", "minDate", "maxDate", "dateFilter", "dateClass", "comparisonStart", "comparisonEnd", "startDateAccessibleName", "endDateAccessibleName"], outputs: ["selectedChange", "yearSelected", "monthSelected", "viewChanged", "_userSelection", "_userDragDrop"], exportAs: ["matCalendar"] }, { kind: "component", type: MatDatepicker, selector: "mat-datepicker", exportAs: ["matDatepicker"] }, { kind: "directive", type: MatDatepickerInput, selector: "input[matDatepicker]", inputs: ["matDatepicker", "min", "max", "matDatepickerFilter"], exportAs: ["matDatepickerInput"] }, { kind: "component", type: MatDatepickerToggle, selector: "mat-datepicker-toggle", inputs: ["for", "tabIndex", "aria-label", "disabled", "disableRipple"], exportAs: ["matDatepickerToggle"] }, { kind: "directive", type: MatDatepickerToggleIcon, selector: "[matDatepickerToggleIcon]" }, { kind: "ngmodule", type: MatDatepickerModule }, { kind: "ngmodule", type: MatNativeDateModule }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDatePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-date-picker', encapsulation: ViewEncapsulation.None, imports: [CommonModule, FormsModule, NgClass, ReactiveFormsModule, CentricIcon2Component, ...materialComponents$1], providers: [WINDMILL_DATE_PICKER_VALUE_ACCESSOR, { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }], standalone: true, template: "@if (readonly) {\r\n  <div\r\n    class=\"windmill-input is-readonly\"\r\n    [class.has-value]=\"value\"\r\n    [ngClass]=\"size\">\r\n    <input\r\n      #inputReadonlyElement\r\n      class=\"input-element\"\r\n      type=\"text\"\r\n      [ngModel]=\"readonlyValue\"\r\n      [readonly]=\"readonly\"\r\n      [attr.title]=\"title\"\r\n      [attr.id]=\"id\"\r\n      [attr.data-testid]=\"testInputId\"\r\n      [attr.placeholder]=\"readonlyPlaceholder\"\r\n      [attr.aria-label]=\"ariaLabel\"\r\n      [attr.aria-labelledby]=\"ariaLabelledby\"\r\n      [attr.aria-description]=\"ariaDescription\"\r\n      [attr.aria-describedby]=\"ariaDescribedby\" />\r\n  </div>\r\n} @else {\r\n  <div\r\n    class=\"windmill-date-picker\"\r\n    [ngClass]=\"size\"\r\n    [class.windmill-input]=\"!calendarOnly\"\r\n    [class.is-disabled]=\"isDisabled\"\r\n    [class.has-value]=\"value\"\r\n    [class.input-invalid]=\"isInvalidDatePicker\"\r\n    [class.has-clear-icon]=\"hasClearIcon\"\r\n    [style.backgroundColor]=\"backgroundColorValue\">\r\n    @if (calendarOnly) {\r\n      <mat-calendar\r\n        class=\"windmill-calendar standalone-calendar\"\r\n        [class.has-border]=\"hasBorderClass\"\r\n        [class.has-shadow]=\"hasShadow\"\r\n        [startView]=\"startView\"\r\n        [startAt]=\"startAt\"\r\n        [selected]=\"value\"\r\n        [minDate]=\"min\"\r\n        [maxDate]=\"max\"\r\n        [dateFilter]=\"datepickerFilter\"\r\n        (yearSelected)=\"emitYearSelected($event)\"\r\n        (monthSelected)=\"emitMonthSelected($event)\"\r\n        (selectedChange)=\"onSelectedChange($event)\">\r\n      </mat-calendar>\r\n    } @else {\r\n      <input\r\n        #inputElement\r\n        class=\"input-element\"\r\n        [formControl]=\"getFormControl()\"\r\n        [id]=\"id\"\r\n        [min]=\"min\"\r\n        [max]=\"max\"\r\n        [matDatepicker]=\"pickerName\"\r\n        [matDatepickerFilter]=\"datepickerFilter\"\r\n        [attr.placeholder]=\"placeholderValue\"\r\n        [tabindex]=\"tabIndexValue\"\r\n        [disabled]=\"isDisabled\"\r\n        [name]=\"name\"\r\n        [autocomplete]=\"autocomplete\"\r\n        [attr.title]=\"title\"\r\n        [attr.data-testid]=\"testInputId\"\r\n        [attr.aria-label]=\"ariaLabel\"\r\n        [attr.aria-labelledby]=\"ariaLabelledby\"\r\n        [attr.aria-description]=\"ariaDescription\"\r\n        [attr.aria-describedby]=\"ariaDescribedby\"\r\n        [attr.aria-invalid]=\"isInvalidDatePicker\"\r\n        [attr.aria-required]=\"isFormControlRequired\"\r\n        (keydown.enter)=\"onKeyDownEnter($event)\"\r\n        (input)=\"onInputChange($event)\"\r\n        (dateInput)=\"onDateInput($event)\"\r\n        (dateChange)=\"onDateChange($event)\"\r\n        (blur)=\"onBlur()\" />\r\n      @if (hasClearButton) {\r\n        <button\r\n          class=\"clear-button\"\r\n          type=\"button\"\r\n          [attr.aria-label]=\"ariaLabelClearIcon\"\r\n          (click)=\"onClearValue()\">\r\n          <centric-icon2\r\n            class=\"clear-icon\"\r\n            [size]=\"'16'\"\r\n            [svgIcon]=\"'cancel_r'\">\r\n          </centric-icon2>\r\n        </button>\r\n      }\r\n      <mat-datepicker-toggle\r\n        matSuffix\r\n        [for]=\"pickerName\"\r\n        [disabled]=\"disableDatepicker\">\r\n        <centric-icon2\r\n          class=\"icon-date-picker\"\r\n          matDatepickerToggleIcon\r\n          [class.disabled]=\"disableDatepicker\"\r\n          [svgIcon]=\"'calendar_b'\"\r\n          [size]=\"datePickerIconSize\">\r\n        </centric-icon2>\r\n      </mat-datepicker-toggle>\r\n      <mat-datepicker\r\n        #pickerName\r\n        [panelClass]=\"'windmill-calendar'\"\r\n        [startView]=\"startView\"\r\n        [startAt]=\"startAt\"\r\n        [disabled]=\"isDisabledDatePickerPopup\"\r\n        [opened]=\"isOpened\"\r\n        (yearSelected)=\"onYearSelected($event)\"\r\n        (monthSelected)=\"onMonthSelected($event)\"\r\n        (closed)=\"onCloseCalendar($event)\"\r\n        (opened)=\"onOpenCalendar($event)\">\r\n      </mat-datepicker>\r\n    }\r\n  </div>\r\n}\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (showValidationMessages) {\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  }\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.Injector, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Injector)]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DATE_FORMATS]
                }] }, { type: i1.DateAdapter }, { type: i0.ChangeDetectorRef }], propDecorators: { disableDatepicker: [{
                type: Input
            }], disableInput: [{
                type: Input
            }], pickerName: [{
                type: Input
            }], startView: [{
                type: Input
            }], startAt: [{
                type: Input
            }], max: [{
                type: Input
            }], pickerType: [{
                type: Input
            }], calendarOnly: [{
                type: Input
            }], hasBorder: [{
                type: Input
            }], hasShadow: [{
                type: Input
            }], name: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], datepickerFilter: [{
                type: Input
            }], min: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], yearSelected: [{
                type: Output
            }], monthSelected: [{
                type: Output
            }], selectedDate: [{
                type: Output
            }], dateInput: [{
                type: Output
            }], dateChange: [{
                type: Output
            }], innerBlur: [{
                type: Output
            }], opened: [{
                type: Output
            }], closed: [{
                type: Output
            }], inputElement: [{
                type: ViewChild,
                args: ['inputElement']
            }], inputReadonlyElement: [{
                type: ViewChild,
                args: ['inputReadonlyElement']
            }], pickerNameElement: [{
                type: ViewChild,
                args: ['pickerName']
            }], onPopState: [{
                type: HostListener,
                args: ['window:popstate']
            }] } });

const materialComponents = [MatDatepickerModule, MatNativeDateModule];
const windmillModules = [CentricIcon2Module, DirectivesModule];
const moduleComponents = [WindmillDatePickerComponent];
class WindmillDatePickerModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDatePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillDatePickerModule, imports: [CommonModule, i3.FormsModule, ReactiveFormsModule, MatDatepickerModule, MatNativeDateModule, CentricIcon2Module, DirectivesModule, WindmillDatePickerComponent], exports: [WindmillDatePickerComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDatePickerModule, providers: [{ provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }], imports: [CommonModule,
            FormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
            ReactiveFormsModule, materialComponents, windmillModules, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillDatePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
                        ReactiveFormsModule,
                        ...materialComponents,
                        ...windmillModules,
                        ...moduleComponents
                    ],
                    exports: [...moduleComponents],
                    providers: [{ provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { float: 'never' } }],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WINDMILL_DATE_PICKER_VALUE_ACCESSOR, WindmillDatePickerComponent, WindmillDatePickerModule };
